/**
 * Perguntas dos questionários do cliente — fixas aqui no front, salvas como JSON dinâmico.
 *
 * Cada item é uma pergunta (ou um título de seção). O que for respondido é gravado em
 * `answers[name]`; perguntas escondidas por `showIf` são removidas do JSON no envio.
 * Os `name` são os mesmos do formulário antigo: o backend os converte para o formato legado
 * que o app mobile consome (LegacyQuestionnaireConverter.java) — não renomear sem ajustar lá.
 *
 * Campos:
 *   type       section | radio | select | text | textarea | password | date | year | number |
 *              inss | checkText | checkboxes | terrains
 *   showIf     (answers) => boolean — pergunta condicional
 *   required   boolean | (answers) => boolean
 *   validate   (value, answers) => mensagem de erro | null
 *   options    string[] (radio/select/checkboxes)
 *   fields     checkText: [{ name, placeholder, maxLength }] — caixa que, marcada, abre os campos
 */

export const QUESTIONNAIRE_TYPES = {
  APOSENTADORIA: "APOSENTADORIA",
  PENSAO: "PENSAO",
};

export const QUESTIONNAIRE_TYPE_LABELS = {
  APOSENTADORIA: "Aposentadoria",
  PENSAO: "Pensão por morte",
};

const YES_NO = ["Sim", "Não"];
const YES_NO_DK = ["Sim", "Não", "Não sei responder"];

const RETIRED = "Já sou aposentado";
const WANTS_TO_RETIRE = "Gostaria de me aposentar";
const OWN_REGIME = "Sim, servidor com regime próprio";

export const EPI_OPTIONS = [
  "Abafadores de ruídos ou Protetor auricular",
  "Máscara e filtro",
  "Luvas / Mangotes",
  "Capacete",
  "Cintos de segurança e cinturões",
  "Óculos de segurança / Viseira",
  "Botas / Sapatos / Botinas",
];

export const TERRAIN_CONDITIONS = [
  "Proprietário", "Possuidor", "Comodatário", "Arrendatário", "Parceiro", "Meeiro",
  "Usufrutuário", "Condômino", "Posseiro", "Assentado", "Acampado",
];

const DANGEROUS_AGENTS = [
  { label: "Ruído", fields: [
    { name: "dangerousNoiseCompany", placeholder: "Empresa" },
    { name: "dangerousNoiseMachine", placeholder: "Máquina" },
  ] },
  { label: "Calor", fields: [{ name: "dangerousHeat", placeholder: "Empresa" }] },
  { label: "Frio", fields: [{ name: "dangerousCold", placeholder: "Empresa" }] },
  { label: "Eletricidade", fields: [{ name: "dangerousElectricity", placeholder: "Empresa" }] },
  { label: "Agentes químicos", fields: [{ name: "dangerousChemicalAgents", placeholder: "Empresa" }] },
  { label: "Trabalho em altura", fields: [{ name: "dangerousHeight", placeholder: "Empresa" }] },
  { label: "Com porte de arma de fogo", fields: [{ name: "dangerousGunBearing", placeholder: "Empresa" }] },
  { label: "Agentes biológicos", fields: [{ name: "dangerousBiologicalAgents", placeholder: "Empresa" }] },
];

const DANGEROUS_FIELD_NAMES = DANGEROUS_AGENTS.flatMap((agent) => agent.fields.map((f) => f.name));

const isRetired = (a) => a.isRetired === RETIRED;
const wantsToRetire = (a) => a.isRetired === WANTS_TO_RETIRE;
const dangerous = (a) => a.optionDangerous === "Sim";
const agriculture = (a) => a.optionAgriculture === "Sim";
const fishing = (a) => a.familyEconomyRegime === "Sim";

/** "dd/MM/yyyy" ou "yyyy" -> número comparável (yyyymmdd); null se incompleto */
function comparable(value) {
  if (!value) return null;
  if (/^\d{4}$/.test(value)) return Number(value) * 10000;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  return m ? Number(m[3] + m[2] + m[1]) : null;
}

/** término não pode ser antes do início */
const endAfter = (startName) => (value, answers) => {
  const start = comparable(answers[startName]);
  const end = comparable(value);
  return start && end && end < start ? "A data de término deve ser posterior à de início." : null;
};

export const RETIREMENT_QUESTIONS = [
  { type: "section", label: "Situação previdenciária" },
  {
    name: "isRetired", type: "radio", required: true,
    label: "O senhor(a) já é aposentado ou gostaria de se aposentar?",
    options: [RETIRED, WANTS_TO_RETIRE],
  },
  { name: "benefitNumber", type: "inss", label: "Número do benefício", required: true, showIf: isRetired },
  { name: "benefitYear", type: "year", label: "Ano da concessão", required: true, showIf: isRetired },
  {
    name: "publicServer", type: "radio", required: true, showIf: wantsToRetire,
    label: "Você é ou já foi servidor público?",
    options: ["Sim, servidor CLT", "Não sei responder", OWN_REGIME, "Não"],
  },
  {
    type: "checkText", label: "Já é aposentado em algum regime?",
    showIf: (a) => wantsToRetire(a) && a.publicServer === OWN_REGIME,
    fields: [{ name: "regime", placeholder: "Descrição", maxLength: 200 }],
  },
  {
    name: "tryRetirement", type: "radio", required: true, showIf: wantsToRetire, options: YES_NO_DK,
    label: "Já fez alguma tentativa de encaminhamento de aposentadoria?",
  },
  {
    name: "retirementStudy", type: "radio", required: true, showIf: wantsToRetire, options: YES_NO_DK,
    label: "Já fez algum estudo sobre sua aposentadoria?",
  },
  {
    name: "workProcess", type: "radio", required: true, options: YES_NO_DK,
    label: "Já teve processo trabalhista, para reconhecimento de algum vínculo?",
  },

  { type: "section", label: "Vida profissional" },
  { name: "militaryService", type: "radio", required: true, options: YES_NO, label: "Já prestou serviço militar?" },
  {
    name: "militaryYearStart", type: "date", label: "Data de início", required: true,
    showIf: (a) => a.militaryService === "Sim",
  },
  {
    name: "militaryYearEnd", type: "date", label: "Data de término", required: true,
    showIf: (a) => a.militaryService === "Sim", validate: endAfter("militaryYearStart"),
  },
  { name: "technicalSchool", type: "radio", required: true, options: YES_NO_DK, label: "Fez escola Técnica?" },
  {
    name: "technicalSchoolYearStart", type: "date", label: "Data de início", required: true,
    showIf: (a) => a.technicalSchool === "Sim",
  },
  {
    name: "technicalSchoolYearEnd", type: "date", label: "Data de término", required: true,
    showIf: (a) => a.technicalSchool === "Sim", validate: endAfter("technicalSchoolYearStart"),
  },
  {
    name: "optionDangerous", type: "radio", required: true, options: YES_NO_DK,
    label: "Trabalhou com insalubridade e periculosidade?",
    validate: (value, a) =>
      value === "Sim" && !DANGEROUS_FIELD_NAMES.some((name) => a[name])
        ? "Um ou mais agentes devem ser selecionados."
        : null,
  },
  ...DANGEROUS_AGENTS.map((agent) => ({
    type: "checkText", label: agent.label, showIf: dangerous, half: true,
    fields: agent.fields.map((f) => ({ ...f, maxLength: 100 })),
  })),
  {
    name: "optionEPI", type: "radio", required: true, options: YES_NO,
    label: "Você faz/fazia o uso de EPI (Equipamento de Proteção Individual)?",
  },
  {
    name: "epis", type: "checkboxes", label: "Quais equipamentos?", options: EPI_OPTIONS,
    showIf: (a) => a.optionEPI === "Sim", required: true,
  },
  {
    name: "beforeAprilNineFive", type: "text", maxLength: 200,
    label: "Trabalhou, antes de 28/04/1995, em quais profissões e atividades?",
  },
  {
    name: "optionTwoPlaces", type: "radio", required: true, options: YES_NO_DK,
    label: "Trabalhou em dois locais simultaneamente?",
  },
  {
    name: "twoPlaceCompanies", type: "text", label: "Quais empresas?", required: true, maxLength: 100,
    placeholder: "Informe as empresas nas quais você trabalhou",
    showIf: (a) => a.optionTwoPlaces === "Sim",
  },

  { type: "section", label: "Atividade rural" },
  { name: "optionAgriculture", type: "radio", required: true, options: YES_NO, label: "Trabalhou na agricultura?" },
  {
    name: "weddingDate", type: "date", label: "Data do casamento ou união estável", required: true,
    showIf: agriculture,
  },
  {
    name: "periodWorked", type: "text", maxLength: 255, showIf: agriculture,
    label: "Qual período trabalhou na agricultura",
  },
  {
    name: "agricultureWorkAlone", type: "radio", required: true, options: YES_NO, showIf: agriculture,
    label: "Trabalhou sozinho na agricultura?",
  },
  {
    name: "workPartner", type: "text", required: true,
    showIf: (a) => agriculture(a) && a.agricultureWorkAlone === "Não",
    label: "Indique com quais membros da família trabalhou? Descreva (irmãos, pais, sogros, esposo(a), tios, avós etc.)",
  },
  {
    name: "indicatedMenbersWorkInCity", type: "radio", required: true, options: YES_NO, showIf: agriculture,
    label: "Algum dos membros indicados trabalhou no meio urbano?",
  },
  {
    name: "workPartnerPerido", type: "text", label: "Qual período?", required: true,
    showIf: (a) => agriculture(a) && a.indicatedMenbersWorkInCity === "Sim",
  },
  {
    name: "haveDocumentsForProof", type: "radio", required: true, options: YES_NO, showIf: agriculture,
    label: "Você possui documentos para comprovar o rural?",
  },
  { name: "terrains", type: "terrains", label: "Em quantos terrenos trabalhou?", showIf: agriculture },
  {
    name: "employeeOrServiceProvider", type: "radio", required: true, options: YES_NO, showIf: agriculture,
    label: "Possui empregado(s) ou prestador(es) de serviço?",
  },
  {
    name: "parentsFarmer", type: "radio", required: true, options: YES_NO, showIf: agriculture,
    label: "Seus pais se aposentaram como agricultores?",
  },
  {
    type: "checkText", label: "Participa de cooperativa?", showIf: agriculture,
    fields: [{ name: "optionCooperative", placeholder: "Informe qual", maxLength: 100 }],
  },
  {
    type: "checkText", label: "Exerce ou exerceu outra atividade e/ou recebe/recebeu outra renda?",
    showIf: agriculture,
    fields: [{ name: "optionActivityOrExtraIncome", placeholder: "Informe qual", maxLength: 220 }],
  },
  {
    type: "checkText", showIf: agriculture,
    label: "Recebe/recebeu outra renda nas seguintes atividades: atividade turística, artística, artesanal, dirigente sindical ou de cooperativa, mandato de vereador?",
    fields: [{ name: "optionActivityIncome", placeholder: "Informe qual", maxLength: 220 }],
  },

  { type: "section", label: "Pesca artesanal" },
  {
    name: "familyEconomyRegime", type: "radio", required: true, options: YES_NO,
    label: "Trabalhou na Pesca em Regime de Economia Familiar?",
  },
  {
    name: "weddingDateFishing", type: "date", label: "Data do casamento ou união estável", required: true,
    showIf: fishing,
  },
  { name: "artisanalFishing", type: "text", maxLength: 255, showIf: fishing, label: "Qual período trabalhou na pesca artesanal" },
  {
    name: "haveDocumentsForProofFishing", type: "radio", required: true, options: YES_NO, showIf: fishing,
    label: "Você possui documentos para comprovar a pesca artesanal?",
  },
  { name: "vesselType", type: "text", maxLength: 255, showIf: fishing, label: "Tipo da embarcação que era utilizada?" },
  { name: "vesselMeasure", type: "text", maxLength: 255, showIf: fishing, label: "Quais as medidas da embarcação?" },
  { name: "ownVessel", type: "text", maxLength: 255, showIf: fishing, label: "A embarcação era própria ou de terceiros?" },
  { name: "fishSpecies", type: "text", maxLength: 255, showIf: fishing, label: "Quais as espécies de peixe?" },
  { name: "howFishWasDone", type: "text", maxLength: 255, showIf: fishing, label: "Descreva como era realizada a pesca?" },
  { name: "whoWorksWithYou", type: "text", maxLength: 255, showIf: fishing, label: "Quem trabalha com você na pesca?" },
  {
    name: "otherIncome", type: "text", maxLength: 255, showIf: fishing,
    label: "Recebe/recebeu outra renda nas seguintes atividades: atividade turística, artística, artesanal, dirigente sindical ou de cooperativa, mandato de vereador?",
  },
  { name: "cooperative", type: "text", maxLength: 255, showIf: fishing, label: "Participavam de cooperativa?" },
  {
    name: "haveAnEmployeeOrServiceProvider", type: "radio", required: true, options: YES_NO, showIf: fishing,
    label: "Possui empregado(s) ou prestador(es) de serviço?",
  },
  {
    name: "fishingParents", type: "radio", required: true, options: YES_NO, showIf: fishing,
    label: "Seus pais se aposentaram como pescadores?",
  },

  { type: "section", label: "Saúde e benefícios" },
  { name: "deficiency", type: "radio", required: true, options: YES_NO, label: "Possui alguma deficiência?" },
  {
    name: "deficiencyName", type: "text", label: "Qual?", required: true, maxLength: 60,
    placeholder: "Informe a deficiência", showIf: (a) => a.deficiency === "Sim",
  },
  {
    name: "deficiencyYearStart", type: "year", label: "Desde quando? (ano)", required: true,
    showIf: (a) => a.deficiency === "Sim",
  },
  {
    name: "disabledWorker", type: "radio", required: true, options: YES_NO_DK,
    label: "Já trabalhou na condição de pessoa com deficiência?",
  },
  {
    name: "accidentHelp", type: "radio", required: true, options: YES_NO_DK,
    label: "Você já recebeu algum auxílio acidente?",
  },
  {
    name: "accidentHelpYearStart", type: "year", label: "Ano de início", required: true,
    showIf: (a) => a.accidentHelp === "Sim",
  },
  {
    name: "accidentHelpYearEnd", type: "year", label: "Ano de término", required: true,
    showIf: (a) => a.accidentHelp === "Sim", validate: endAfter("accidentHelpYearStart"),
  },
  {
    name: "accidentHelpOfficeClient", type: "radio", required: true, options: YES_NO,
    label: "É cliente de auxílio acidente do escritório?",
  },
  { name: "inssPassword", type: "password", label: "Senha do INSS", maxLength: 255, placeholder: "Informe a sua senha do INSS" },
];

const REQUEST_REASONS = ["Óbito", "Morte Presumida"];

const KINSHIP_DEGREES = [
  "Casado(a) com certidão de casamento",
  "Convivia em união estável",
  "Separado(a)/divorciado(a)",
  "Filho(a), menor de 21 (vinte e um) anos",
  "Filho(a), maior de 21 (vinte e um) anos, inválido ou que tenha deficiência intelectual ou mental ou deficiência grave",
  "Pai/mãe que dependiam financeiramente do pessoal falecida",
  "Irmão(a) maior de 21 anos que dependiam financeiramente do pessoal falecida",
  "Outros",
];

const DECEASED_ACTIVITIES = [
  "Empregado(a)",
  "Agricultor(a)/Pescador(a)",
  "Autônomo(a)",
  "Servidor(a) Público(a)",
  "Desempregado / recebendo seguro desemprego",
  "Desempregado",
];

export const PENSION_QUESTIONS = [
  { type: "section", label: "Sobre o óbito" },
  { name: "requestReason", type: "select", required: true, options: REQUEST_REASONS, label: "Qual o motivo do pedido?" },
  { name: "dateOfDeath", type: "date", required: true, label: "Qual a data do óbito?" },
  { name: "causeOfDeath", type: "text", required: true, maxLength: 255, label: "Qual a causa do óbito?" },
  {
    name: "deathCertificate", type: "radio", required: true, options: YES_NO_DK,
    label: "Você possui a Certidão de óbito, ou documento que comprove a morte presumida do falecido?",
  },
  {
    name: "occurInTheExerciseOfFunction", type: "radio", required: true, options: YES_NO_DK,
    label: "O óbito ocorreu no exercício da função (labor)?",
  },

  { type: "section", label: "Sobre o(a) falecido(a)" },
  {
    name: "workingInDateOfDeath", type: "radio", required: true, options: YES_NO_DK,
    label: "O(A) falecido(a) estava trabalhando na data do óbito?",
  },
  {
    name: "deceasedActivity", type: "radio", required: true, options: DECEASED_ACTIVITIES,
    label: "Que tipo de atividade era exercida pelo(a) falecido(a)?",
  },
  {
    name: "regimeOnDateOfDeath", type: "radio", required: true, options: YES_NO_DK,
    label: "O(A) falecido(a) contribuía para algum regime de previdência, na data do óbito?",
  },
  {
    name: "regimeOnDateOfDeathDescription", type: "text", label: "Quais?", maxLength: 100,
    showIf: (a) => a.regimeOnDateOfDeath === "Sim",
  },
  { name: "wasRetired", type: "radio", required: true, options: YES_NO, label: "O(A) falecido(a) era aposentado(a)?" },
  {
    name: "wasRetiredBenefitNumber", type: "inss", label: "Número do benefício (aposentadoria)",
    showIf: (a) => a.wasRetired === "Sim",
  },
  { name: "receivedBenefit", type: "radio", required: true, options: YES_NO, label: "O(A) falecido(a) estava recebendo benefício?" },
  { name: "deceasedINSSPassword", type: "password", required: true, maxLength: 255, label: "Senha do MEU INSS do falecido" },

  { type: "section", label: "Sobre o requerente" },
  {
    name: "degreeOfKinship", type: "select", required: true, options: KINSHIP_DEGREES,
    label: "Qual seu grau de parentesco com o(a) falecido(a)?",
  },
  {
    name: "kinshipDocuments", type: "radio", required: true, options: YES_NO,
    label: "Você possui documentos para comprovar a relação de parentesco?",
  },
  {
    name: "kinshipDocumentsDescription", type: "text", label: "Quais?", maxLength: 100,
    showIf: (a) => a.kinshipDocuments === "Sim",
  },
  {
    name: "economicDependencyDocuments", type: "radio", required: true, options: YES_NO,
    label: "Você possui documentos para comprovar a dependência econômica?",
  },
  {
    name: "economicDependencyDocumentsDescription", type: "text", label: "Quais?", maxLength: 100,
    showIf: (a) => a.economicDependencyDocuments === "Sim",
  },
  {
    name: "hasDisability", type: "text", required: true, maxLength: 255,
    label: "Você é uma pessoa com deficiência intelectual ou mental ou deficiência grave?",
  },
  {
    name: "youGetSomeBenefit", type: "radio", required: true, options: YES_NO_DK,
    label: "Você já recebe algum benefício do INSS? Ou de algum outro Regime da Previdência?",
  },
  {
    name: "youGetSomeBenefitDescription", type: "text", label: "Qual?", maxLength: 100,
    showIf: (a) => a.youGetSomeBenefit === "Sim",
  },
  { name: "applicantINSSPassword", type: "password", required: true, maxLength: 255, label: "Senha do MEU INSS do requerente" },

  { type: "section", label: "Dependentes" },
  { name: "hasDependentsUnder18", type: "radio", required: true, options: YES_NO, label: "Deixou filhos menores de 21 anos?" },
  {
    name: "childrenHasBenefits", type: "radio", required: true, options: YES_NO,
    label: "Algum dos filhos recebe benefício do governo (Ex.: BPC - LOAS)?",
    showIf: (a) => a.hasDependentsUnder18 === "Sim",
  },
  {
    name: "childrenHasBenefitsNames", type: "text", label: "Indique o nome dele(s):", required: true, maxLength: 255,
    showIf: (a) => a.hasDependentsUnder18 === "Sim" && a.childrenHasBenefits === "Sim",
  },
];

export const QUESTIONS_BY_TYPE = {
  APOSENTADORIA: RETIREMENT_QUESTIONS,
  PENSAO: PENSION_QUESTIONS,
};
